<template>
	<DialogPanel
		class="template-preview-panel"
		:scroll-body="true"
		@close="$emit( `close` )"
	>
		<template #header-left>
			<div class="sub-header">
				<div class="sub-header--left">
					<BackButton
						:disabled="disabled"
						size="SMALL"
						@click="$emit( `back` )"
					/>
				</div>
				<div class="sub-header--right">
					<div class="breadcrumbs">
						<Breadcrumbs
							:crumbs="crumbs"
							:disabled="disabled"
							@activate="onBreadcrumbActivate"
						/>
					</div>
					<h1 class="title">{{ template.title }}</h1>
					<p class="description">{{ template.description }}</p>
				</div>
			</div>
		</template>
		<template #body>
			<div class="template-content">
				<div class="disabled-content"></div>
				<EditorContent :editor="editor" />
			</div>
		</template>
		<template #footer>
			<TeamingButton
				size="SMALL"
				@click="onApplyClicked"
			>
				{{ translate("applyButton") }}
			</TeamingButton>
			<TeamingButton
				size="SMALL"
				type="QUATERNARY"
				@click="$emit( `close` )"
			>
				{{ translate("cancelButton") }}
			</TeamingButton>
		</template>
	</DialogPanel>
</template>

<script>
import { Editor, EditorContent } from "@tiptap/vue-3";
import StarterKit from "@tiptap/starter-kit";
import Image from "@tiptap/extension-image";
import Link from "@tiptap/extension-link";
import Underline from "@tiptap/extension-underline";
import TiptapSlashCommands from "@teaming/tiptap-slash-commands";
import TeamingCreatedOnAttribute from "@teaming/tiptap-created-on-attribute";
import UuidExtension from "@teaming/tiptap-uuid-attribute";
import TeamingCreatorAttribute from "@teaming/tiptap-creator-attribute";
import TeamingTiptapTopic from "@teaming/tiptap-topic";
import TeamingTiptapCommitment from "@teaming/tiptap-commitment";
import TeamingTiptapDecision from "@teaming/tiptap-decision";

import {
	BackButton,
	Breadcrumbs,
	DialogPanel,
	TeamingButton,
	makeCrumb
} from "@teaming/common-ui";

const staticExtensions = [
	StarterKit.configure( { history: false, } ),
	Image.configure( {
		HTMLAttributes: {
			class: "teaming-text-editor__image",
		},
		inline: true,
	} ),
	Link,
	Underline,
	TiptapSlashCommands,
	TeamingCreatedOnAttribute,
	UuidExtension,
];

export default {
	name: "TemplatePreviewPanel",
	emits: [ "apply", "close", "back", ],
	components: {
		TeamingButton,
		BackButton,
		Breadcrumbs,
		EditorContent,
		DialogPanel,
	},
	provide() {
		return {
			context: this.context,
		};
	},
	props: {
		disabled: {
			type: Boolean,
			default: false,
		},
		context: {
			type: Object,
			required: true,
		},
		template: {
			type: Object,
			required: true,
		},
	},
	data() {
		return {
			editor: null,
		};
	},
	computed: {
		crumbs() {
			const { translate, template, } = this;
			return [
				makeCrumb( {
					text: translate( "backBreadcrumb" ),
					route: "back",
				} ),
				makeCrumb( {
					text: translate( "templateBreadcrumb", template ),
					isActive: true,
				} ),
			];
		},
	},
	methods: {
		translate( key, args = {} ) {
			const { name, } = this.$options;
			return this.$t( `common-team.components.${ name }.${ key }`, args );
		},
		onBreadcrumbActivate( { route, } ) {
			if ( route === "back" ) {
				this.$emit( "back" );
			}
		},
		onApplyClicked() {
			const { template, } = this;
			this.$emit( "apply", template );
		},
	},
	async mounted() {
		const { content, } = this.template;
		const noop = () => {};
		this.editor = new Editor( {
			extensions: [
				...staticExtensions,
				TeamingCreatorAttribute.configure( { getPersonId: noop, } ),
				TeamingTiptapTopic.configure( { userName: "", onTeamingExtensionCallback: noop, } ),
				TeamingTiptapCommitment.configure( { onTeamingExtensionCallback: noop, } ),
				TeamingTiptapDecision.configure( { onTeamingExtensionCallback: noop, } ),
			],
		} );
		this.editor.setEditable( false );
		await this.$nextTick();
		this.editor.commands.setContent( content );
	},
	async updated() {
		const { content, } = this.template;
		await this.$nextTick();
		this.editor.commands.setContent( content );
	},
	beforeUnmount() {
		this.editor = null;
	},
};
</script>

<style lang="scss" scoped>
$footerBackgroundColor: $color1-050;
$titleColor: $color1-700;
$descriptionColor: $color1-500;

.template-preview-panel {
	display: flex;
	flex-direction: column;
	justify-content: flex-start;

	position: absolute;
	top: 0;
	bottom: 0;
	width: 100%;
}

.template-preview-panel .sub-header {
	display: flex;
	flex-direction: row;
	justify-content: flex-start;
	align-items: stretch;
	gap: 0.5rem;
}

.template-preview-panel .sub-header--left {
	display: flex;
	flex-direction: column;
	justify-content: center;
	align-items: flex-start;
}

.template-preview-panel .sub-header--right {
	flex: 1;

	display: flex;
	flex-direction: column;
	justify-content: center;

	text-align: left;
}

.template-preview-panel .title {
	@include font-and-line-for(2);
	color: $titleColor;
	font-weight: 600;
}

.template-preview-panel .description {
	@include font-and-line-for(0);
	color: $descriptionColor;
	font-weight: 450;
}

.template-preview-panel .template-content {
	position: relative;
}

.template-preview-panel .disabled-content {
	z-index: 10;
	position: absolute;
	top: 0;
	bottom: 0;
	left: 0;
	width: 100%;
}
</style>

<style lang="scss">
.template-preview-panel .body {
	padding: 1rem;
}
</style>
